import AddressAutocomplete from '@orn/shared-web/components/AddressAutocomplete';
import Input from '@orn/shared-web/components/Input/Input';
import Select, { type SelectItemType } from '@orn/shared-web/components/Select/Select';
import SelectItem from '@orn/shared-web/components/Select/SelectItem';
import YesNoToggle from '@orn/shared-web/components/YesNoToggle';
import formatLocation from '@orn/shared-web/utils/formatLocation';
import * as React from 'react';

import { HousingOwnership, ResidenceType } from '../../../../__generated__/globalTypes';
import { HOME_OWNERSHIP_OPTIONS, RESIDENCE_TYPE_OPTIONS } from '../../../../lib/dependants/constants';
import { toResidenceAddress } from '../../../../lib/dependants/residenceAddress';
import { OnboardingTitle } from '../../../OnboardingLayout';
import { getDependantPossessive, getDisplayName } from '../dependantUtils';
import OnboardingStepActions from '../OnboardingStepActions';

import FrequentLocationsForm from './FrequentLocationsForm';
import {
  isPartnershipLivingApart,
  type IFrequentLocationsState,
  type IResidenceDraft,
  type IResidenceFormState,
} from './residenceDraft';

import type { IResidence } from '../../../../hooks/queries/useDependantResidences';
import type { IDependant } from '../../../../hooks/queries/useDependants';

interface IResidenceAddressFieldsProps {
  state: IResidenceFormState;
  onChange: (next: IResidenceFormState) => void;
  addressLabel: string;
  apiKey: string;
}

/**
 * Address + living-situation + (when independent) home-ownership fields for a
 * single parent group. Rendered only for groups whose "live with you?" answer
 * is No — the caller gates on that, so this never shows its own toggle.
 */
const ResidenceAddressFields = ({ state, onChange, addressLabel, apiKey }: IResidenceAddressFieldsProps) => {
  const update = <K extends keyof IResidenceFormState>(field: K, value: IResidenceFormState[K]) => {
    onChange({ ...state, [field]: value });
  };

  const showOwnership = state.residenceType === ResidenceType.IndependentLiving;

  return (
    <div className="flex w-full flex-col gap-6">
      <AddressAutocomplete
        apiKey={apiKey}
        label={addressLabel}
        placeholder="123 Main St., Apt. 5, San Francisco, CA"
        value={state.addressText}
        // Street-level only — a primary residence must be a full address, so
        // cities/regions never appear as suggestions.
        includedPrimaryTypes={['street_address']}
        onInputChange={(text) =>
          onChange({
            ...state,
            addressText: text,
            address: state.address?.formatted === text ? state.address : null,
          })
        }
        onSelect={(parsed) =>
          onChange({ ...state, addressText: parsed.formattedAddress, address: toResidenceAddress(parsed) })
        }
      />

      <Select
        label="Living situation"
        selectedKey={state.residenceType}
        onSelectionChange={(key) => update('residenceType', key as ResidenceType)}
        items={RESIDENCE_TYPE_OPTIONS}
      >
        {(item: SelectItemType) => <SelectItem id={item.id} label={item.label} />}
      </Select>

      {state.residenceType === ResidenceType.Other && (
        <Input
          label="Describe their living situation"
          placeholder="e.g. Temporary rehabilitation facility"
          value={state.residenceTypeOther}
          onChange={(v) => update('residenceTypeOther', v)}
        />
      )}

      {showOwnership && (
        <>
          <Select
            label="Home ownership"
            placeholder="Select"
            selectedKey={state.homeOwnership ?? undefined}
            onSelectionChange={(key) => update('homeOwnership', key as HousingOwnership)}
            items={HOME_OWNERSHIP_OPTIONS}
          >
            {(item: SelectItemType) => <SelectItem id={item.id} label={item.label} />}
          </Select>

          {state.homeOwnership === HousingOwnership.Other && (
            <Input
              label="Describe the ownership situation"
              placeholder="e.g. Co-owned with sibling"
              value={state.homeOwnershipOther}
              onChange={(v) => update('homeOwnershipOther', v)}
            />
          )}
        </>
      )}
    </div>
  );
};
ResidenceAddressFields.displayName = 'ResidenceAddressFields';

interface IParentCardProps {
  title: string;
  children: React.ReactNode;
}

/** Card wrapper showing whose home a set of address fields belongs to ("Jane's home"). */
const ParentCard = ({ title, children }: IParentCardProps) => (
  <div className="border-secondary flex w-full flex-col gap-4 rounded-xl border p-4">
    <p className="text-primary text-md font-semibold">{title}</p>
    {children}
  </div>
);
ParentCard.displayName = 'ParentCard';

interface IResidenceDetailsPageProps {
  dependants: ReadonlyArray<IDependant>;
  isPartnership: boolean;
  draft: IResidenceDraft;
  /** Dependant A's resolved primary residence, for naming the "away from home" question on first render. */
  dependantAPrimary: IResidence | null;
  apiKey: string;
  onParentAFormChange: (next: IResidenceFormState) => void;
  onParentBFormChange: (next: IResidenceFormState) => void;
  onFrequentLocationsChange: (next: IFrequentLocationsState) => void;
  isContinueDisabled: boolean;
  isContinueLoading: boolean;
  submitError: boolean;
  /** Persists the whole section, then advances to the next wizard screen. */
  onContinue: () => void;
  /** Inline back-arrow handler — returns to the arrangement page with the draft intact. */
  onBack: () => void;
  /** Skips the entire residence section without persisting. */
  onSkip: () => void;
}

/**
 * Page 2 of the residence section — "Where they live". For any loved one not
 * living with the caregiver, collects the address + living situation + (when
 * independent) home ownership; then asks one shared "do they spend time
 * elsewhere?" question across the household. Owns no persistence itself —
 * Continue calls back to the container, which writes the section's rows and
 * advances only if every write succeeds. Purely presentational; all state and
 * navigation come from props.
 */
const ResidenceDetailsPage = ({
  dependants,
  isPartnership,
  draft,
  dependantAPrimary,
  apiKey,
  onParentAFormChange,
  onParentBFormChange,
  onFrequentLocationsChange,
  isContinueDisabled,
  isContinueLoading,
  submitError,
  onContinue,
  onBack,
  onSkip,
}: IResidenceDetailsPageProps) => {
  const dependantA = dependants[0];
  const dependantB = dependants[1] ?? null;
  const aName = getDisplayName(dependantA);
  const bName = dependantB !== null ? getDisplayName(dependantB) : '';

  const apart = isPartnershipLivingApart(draft, isPartnership);
  const showAFields = draft.parentAForm.liveWithCaregiver === false;
  const showBFields = dependantB !== null && draft.parentBForm.liveWithCaregiver === false;

  // Names the home city/state so it's clear what "away" means; an apart
  // partnership has two homes, so it stays generic.
  const homeLocation = apart ? null : formatLocation(draft.parentAForm.address ?? dependantAPrimary);
  const awayLocation = homeLocation ?? 'home';
  const awayFromHomeLabel =
    (dependants.length === 1
      ? `Does ${aName} frequently spend time in a location away from ${awayLocation}?`
      : `Do they frequently spend time in a location away from ${awayLocation}?`) +
    ' This will help us recognize spending away from home.';

  return (
    <>
      <OnboardingTitle title="Where they live" onBack={onBack} />

      <div className="flex w-full flex-col gap-6">
        {apart ? (
          <>
            {showAFields && (
              <ParentCard title={`${aName}'s home`}>
                <ResidenceAddressFields
                  state={draft.parentAForm}
                  onChange={onParentAFormChange}
                  addressLabel={`${aName}'s address`}
                  apiKey={apiKey}
                />
              </ParentCard>
            )}
            {showBFields && (
              <ParentCard title={`${bName}'s home`}>
                <ResidenceAddressFields
                  state={draft.parentBForm}
                  onChange={onParentBFormChange}
                  addressLabel={`${bName}'s address`}
                  apiKey={apiKey}
                />
              </ParentCard>
            )}
          </>
        ) : (
          showAFields && (
            <ResidenceAddressFields
              state={draft.parentAForm}
              onChange={onParentAFormChange}
              addressLabel={`${getDependantPossessive(dependants, "your loved one's")} address`}
              apiKey={apiKey}
            />
          )
        )}

        <YesNoToggle
          label={awayFromHomeLabel}
          value={draft.frequentLocations.toggle}
          onChange={(v) => onFrequentLocationsChange({ ...draft.frequentLocations, toggle: v })}
        />

        {draft.frequentLocations.toggle === true && (
          <FrequentLocationsForm
            places={draft.frequentLocations.places}
            onChange={(next) => onFrequentLocationsChange({ ...draft.frequentLocations, places: next })}
            apiKey={apiKey}
          />
        )}
      </div>

      <div className="flex w-full flex-col items-center gap-5">
        {submitError && (
          <p className="text-error-primary text-sm font-medium">
            Something went wrong saving your answers. Please try again.
          </p>
        )}
        <OnboardingStepActions
          isContinueDisabled={isContinueDisabled}
          isContinueLoading={isContinueLoading}
          onContinue={onContinue}
          onSkip={onSkip}
        />
      </div>
    </>
  );
};

ResidenceDetailsPage.displayName = 'ResidenceDetailsPage';

export default ResidenceDetailsPage;
